import { useMemo } from 'react'

import { GameBackdrop } from '@/features/stadium/GameBackdrop'
import { MatchFocusCard } from '@/features/stadium/MatchFocusCard'
import { StadiumBracket } from '@/features/stadium/StadiumBracket'
import type { Bracket, BracketMatch, RGNEvent } from '@/lib/api/schemas'

/**
 * FINAL_MATCH phase — the pivot moment of the tournament.
 *
 * Same building blocks as LivePhase (bracket + focus card), but the
 * proportions flip: the bracket shrinks to ~25%, the focus card
 * scales to ~75%. The focus card gets a gold accent strip and a
 * "GRAND FINAL" banner so the spectator immediately reads it as the
 * climax — not just "another LIVE match".
 *
 * Per the Emil framework — once-per-tournament moment, can afford
 * richness. The 480ms blur-crossfade in Stadium.tsx handles the
 * LIVE→FINAL_MATCH transition itself; this component just renders
 * the destination layout.
 */
export function FinalMatchPhase({
  event,
  bracket,
  recentlyCompleted,
}: {
  event: RGNEvent
  bracket: Bracket | null
  recentlyCompleted?: ReadonlySet<string>
}) {
  const focusId = useMemo(() => pickFinalMatchId(bracket), [bracket])

  return (
    <div className="relative flex h-full flex-col gap-[clamp(12px,1.6vh,24px)] px-[clamp(16px,1.6vw,32px)] py-[clamp(12px,1.4vh,20px)]">
      <GameBackdrop event={event} mode="paneled" />

      {/* GRAND FINAL banner — gold chevron rays flanking the label.
          Heavier glow + stripe accents make the once-per-tournament
          moment land at venue distance. */}
      <div
        className="relative flex items-center justify-center gap-[clamp(16px,1.8vw,36px)]"
        style={{ minHeight: 'clamp(48px, 6vh, 80px)' }}
      >
        <BannerRays side="left" />
        <span
          className="font-display font-black uppercase"
          style={{
            color: 'var(--tv-gold)',
            letterSpacing: '0.4em',
            fontSize: 'clamp(1.6rem, 2.4vw, 2.6rem)',
            textShadow: '0 0 32px rgba(255, 179, 0, 0.6), 0 4px 12px rgba(0,0,0,0.55)',
          }}
        >
          Grand Final
        </span>
        <BannerRays side="right" />
      </div>

      <div className="relative flex min-h-0 flex-1 gap-[clamp(16px,1.6vw,32px)]">
        <section
          // Bracket pane — shrunk to 25% so the focus card dominates.
          // Same panel chrome as LivePhase for visual continuity.
          className="relative flex h-full min-w-0 basis-[25%] overflow-hidden rounded-2xl border bg-[color-mix(in_oklab,var(--tv-bg-elev)_78%,transparent)]"
          style={{ borderColor: 'var(--tv-border)' }}
          aria-label="Bracket"
        >
          {bracket ? (
            <StadiumBracket
              bracket={bracket}
              focusMatchId={focusId}
              recentlyCompleted={recentlyCompleted}
            />
          ) : null}
        </section>

        <section
          // Focus pane — 75%, gold-tinged border + a soft inner glow so
          // the climax reads at a glance from the back of the venue.
          className="relative flex h-full min-w-0 flex-1 overflow-hidden rounded-2xl border-2 bg-[color-mix(in_oklab,var(--tv-bg-elev)_78%,transparent)]"
          style={{
            borderColor: 'var(--tv-gold)',
            boxShadow: '0 0 60px -16px rgba(255, 179, 0, 0.4), inset 0 0 80px -32px rgba(255, 179, 0, 0.2)',
          }}
          aria-label="Grand Final match"
        >
          <div className="h-full w-full">
            <MatchFocusCard bracket={bracket} />
          </div>
        </section>
      </div>
    </div>
  )
}

/** Find the grand final by `kind === 'GF'` or by being the lone
 *  incomplete match at the highest round. */
function pickFinalMatchId(bracket: Bracket | null): string | null {
  if (!bracket?.matches) return null
  const gf = bracket.matches.find((m) => m.kind === 'GF' && !isComplete(m))
  if (gf) return gf.matchId
  const incomplete = bracket.matches.filter((m) => !isComplete(m))
  if (incomplete.length === 0) return null
  if (incomplete.length === 1) return incomplete[0].matchId
  const maxRound = bracket.matches.reduce(
    (max, m) => (typeof m.round === 'number' && m.round > max ? m.round : max),
    -Infinity,
  )
  const finalCandidate = incomplete.find((m) => m.round === maxRound)
  return finalCandidate?.matchId ?? incomplete[0].matchId
}

function isComplete(m: BracketMatch): boolean {
  const s = (m.status ?? '').toUpperCase()
  return s === 'COMPLETE' || s === 'COMPLETED' || s === 'OVERRIDDEN'
}

/**
 * Decorative gold stripe trio flanking the GRAND FINAL label. Three
 * skewed bars of decreasing opacity — reads as a chevron / motion-line
 * accent without needing SVG. Mirror-flipped for the right side via
 * the `side` prop so the chevrons point inward to the title.
 */
function BannerRays({ side }: { side: 'left' | 'right' }) {
  const isLeft = side === 'left'
  return (
    <div
      aria-hidden
      className="flex items-center gap-[clamp(4px,0.4vw,8px)]"
      style={{ transform: isLeft ? undefined : 'scaleX(-1)' }}
    >
      <span
        className="block"
        style={{
          width: 'clamp(40px, 4vw, 80px)',
          height: 'clamp(4px, 0.4vw, 8px)',
          backgroundColor: 'var(--tv-gold)',
          opacity: 0.3,
          transform: 'skewX(-30deg)',
        }}
      />
      <span
        className="block"
        style={{
          width: 'clamp(28px, 2.8vw, 56px)',
          height: 'clamp(4px, 0.4vw, 8px)',
          backgroundColor: 'var(--tv-gold)',
          opacity: 0.6,
          transform: 'skewX(-30deg)',
        }}
      />
      <span
        className="block"
        style={{
          width: 'clamp(16px, 1.6vw, 32px)',
          height: 'clamp(4px, 0.4vw, 8px)',
          backgroundColor: 'var(--tv-gold)',
          boxShadow: '0 0 20px -2px rgba(255,179,0,0.55)',
          transform: 'skewX(-30deg)',
        }}
      />
    </div>
  )
}
